// ── ReviewerNavbar.js ────────────────────────────────────────────────────────
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReviewerAuth, ReviewerAPI } from '../context/ReviewerAuthContext';
import { useTheme } from '../context/ThemeContext';
import { LayoutDashboard, LogOut, ChevronDown, Sun, Moon, User, ShieldCheck } from 'lucide-react';
import useNotifications from '../hooks/useNotifications';
import NotificationBell from './NotificationBell';

export default function ReviewerNavbar() {
  const { reviewer, logout } = useReviewerAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);
  const notifications = useNotifications({ client: ReviewerAPI, basePath: '/api/reviewer/notifications', enabled: !!reviewer });

  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <nav className="reviewer-navbar" style={s.nav}>
      <div className="reviewer-navbar-inner" style={s.inner}>
        <div style={s.brand} onClick={() => navigate('/reviewer/dashboard')}>
          <img src="/logo.png" alt="Shortflix" style={{ height: 26 }}
            onError={e => { e.target.style.display = 'none'; }} />
          <span style={s.portalBadge}>Reviewer</span>
        </div>

        <button onClick={() => navigate('/reviewer/dashboard')} style={s.navLink}>
          <LayoutDashboard size={13} /> <span className="reviewer-navbar-link-label">My Queue</span>
        </button>

        <div style={s.right}>
          <NotificationBell {...notifications} size={30} onItemClick={() => navigate('/reviewer/dashboard')} />

          <button onClick={toggle} style={s.iconBtn}>
            {theme === 'dark' ? <Sun size={13} color="var(--text-secondary)" /> : <Moon size={13} color="var(--text-secondary)" />}
          </button>

          <div style={{ position: 'relative' }} ref={dropRef}>
            <button onClick={() => setDropOpen(o => !o)} style={s.profileBtn}>
              {reviewer?.avatar_url
                ? <img src={reviewer.avatar_url} alt="" style={s.avatar} />
                : <div style={s.avatarFallback}>{(reviewer?.name || 'R')[0].toUpperCase()}</div>
              }
              <span className="reviewer-navbar-profile-name" style={s.profileName}>{reviewer?.name?.split(' ')[0]}</span>
              <ChevronDown size={12} color="var(--text-muted)"
                style={{ transform: dropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>
            {dropOpen && (
              <div style={s.dropdown} className="fade-in">
                <div style={s.dropHeader}>
                  <div style={s.dropName}>{reviewer?.name}</div>
                  <div style={s.dropEmail}>{reviewer?.specialty || 'Film Reviewer'}</div>
                </div>
                <hr className="divider" />
                <button style={s.dropItem} onClick={() => { navigate('/reviewer/agreement'); setDropOpen(false); }}>
                  <ShieldCheck size={13} color="var(--text-secondary)" />
                  <span>Confidentiality Agreement</span>
                </button>
                <hr className="divider" />
                <button style={s.dropItem} onClick={() => { logout(); navigate('/reviewer'); setDropOpen(false); }}>
                  <LogOut size={13} color="var(--red)" />
                  <span style={{ color: 'var(--red)' }}>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

const s = {
  nav: { background: 'var(--nav-bg)', backdropFilter: 'blur(18px)', borderBottom: '1.5px solid var(--border)', position: 'sticky', top: 0, zIndex: 100 },
  inner: { maxWidth: 1100, margin: '0 auto', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', gap: 20 },
  brand: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flexShrink: 0 },
  portalBadge: { fontSize: 9, fontWeight: 800, color: 'var(--pink)', letterSpacing: '0.12em', textTransform: 'uppercase', background: 'var(--pink-glow)', border: '1.5px solid var(--pink-glow-strong)', borderRadius: 20, padding: '2px 8px' },
  navLink: { background: 'none', color: 'var(--text-secondary)', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 10px', borderRadius: 'var(--r-sm)', transition: 'color 0.15s' },
  right: { display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' },
  iconBtn: { width: 30, height: 30, borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  profileBtn: { display: 'flex', alignItems: 'center', gap: 7, background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 22, padding: '4px 10px 4px 4px', cursor: 'pointer' },
  avatar: { width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' },
  avatarFallback: { width: 24, height: 24, borderRadius: '50%', background: 'var(--pink)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 },
  profileName: { fontSize: 12, fontWeight: 600, color: 'var(--text)' },
  dropdown: { position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 200, background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-lg)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', overflow: 'hidden', zIndex: 200 },
  dropHeader: { padding: '13px 15px 11px' },
  dropName: { fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 2 },
  dropEmail: { fontSize: 11, color: 'var(--text-muted)' },
  dropItem: { width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'none', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'left' },
};
